import { useCallback, useMemo, useState } from "react";
import { AggregationFunction, AggregationsState, useAggregations } from "./useAggregations";
import { Dimensions, DimensionsState, useDimensions } from "./useDimensions";
import { ScoreId, ScoresIdsState, useScoresIds } from "./useScoresIds";
import { Variations, VariationsState, useVariations } from "./useVariations";
import { PrevScoresState, usePrevScores } from "./usePrevScores";
import { useSubscores } from "./useSubscores";

type OlapScoresBaseParams = {
    ids: ScoreId[];
    dimensions: Dimensions;
    variations: Variations;
    aggregations: AggregationFunction[];
    subscores: boolean;
};

type OlapScoresQueryParams = Partial<OlapScoresBaseParams>;

export type OlapScoresQuery = Partial<{
    scores: OlapScoresQueryParams;
    prevScores: boolean;
}>;

type OlapScoresState = {
    setQuery: (query: OlapScoresQuery) => void;
} & ScoresIdsState &
    DimensionsState &
    VariationsState &
    AggregationsState &
    PrevScoresState;

export type OlapScoresParams = {
    query: OlapScoresQuery;
    state: OlapScoresState;
};

export const useOlapScoresParams = (initQuery: OlapScoresQuery): OlapScoresParams => {
    const [manualQuery, setManualQuery] = useState<OlapScoresQuery>(initQuery);

    const scoresIds = useScoresIds(manualQuery.scores?.ids);
    const dimensions = useDimensions(manualQuery.scores?.dimensions);
    const variations = useVariations(manualQuery.scores?.variations);
    const aggregations = useAggregations(manualQuery.scores?.aggregations);
    const subscores = useSubscores(manualQuery.scores?.subscores);
    const prevScores = usePrevScores(manualQuery.prevScores);

    const query = useMemo<OlapScoresQuery>(
        () => ({
            ...manualQuery,
            scores: {
                ...manualQuery.scores,
                ids: scoresIds.query,
                dimensions: dimensions.query,
                variations: variations.query,
                aggregations: aggregations.query,
                subscores: subscores.query,
            },
            prevScores: prevScores.query,
        }),
        [
            manualQuery,
            scoresIds.query,
            dimensions.query,
            variations.query,
            aggregations.query,
            subscores.query,
            prevScores.query,
        ],
    );

    const setQuery = useCallback(
        (next: OlapScoresQuery) => {
            setManualQuery(next);
            if (next.scores?.ids !== undefined) {
                scoresIds.state.setScoresIds(next.scores.ids);
            }
            if (next.scores?.dimensions !== undefined) {
                dimensions.state.setDimensions(next.scores.dimensions);
            }
            if (next.scores && "variations" in next.scores) {
                variations.state.setVariations(next.scores.variations);
            }
            if (next.scores && "aggregations" in next.scores) {
                aggregations.state.setAggregations(next.scores.aggregations);
            }
            if (next.scores?.subscores !== undefined) {
                subscores.state.setSubscores(next.scores.subscores);
            }
            if (next.prevScores !== undefined) {
                prevScores.state.setPrevScores(next.prevScores);
            }
        },
        [scoresIds.state, dimensions.state, variations.state, aggregations.state, subscores.state, prevScores.state],
    );

    return {
        query,
        state: {
            setQuery,
            ...scoresIds.state,
            ...dimensions.state,
            ...variations.state,
            ...aggregations.state,
            ...subscores.state,
            ...prevScores.state,
        },
    };
};
